import React, { useState, useEffect } from 'react';
import api from '../../api';

const CreditRechargeModal = ({ isOpen, onClose, onRechargeSuccess, userId, currentCredits }) => {
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [customCredits, setCustomCredits] = useState('');
  const [packages, setPackages] = useState([]);
  const [calculation, setCalculation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [recharging, setRecharging] = useState(false);

  const fetchPricingPackages = async () => {
    try {
      setLoading(true);
      const response = await api.get('/credits/pricing');
      setPackages(response.data.packages);
    } catch (error) {
      setError('Failed to load pricing information');
    } finally {
      setLoading(false);
    }
  };

  const calculateCustomPricing = async () => {
    try {
      const response = await api.post('/credits/calculate', {
        credits: parseInt(customCredits)
      });
      setCalculation(response.data);
    } catch (error) {
      setCalculation(null);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchPricingPackages();
    }
  }, [isOpen]);

  useEffect(() => {
    if (customCredits && parseInt(customCredits) >= 30) {
      calculateCustomPricing();
    } else {
      setCalculation(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customCredits]);

  const handleRecharge = async (credits) => {
    try {
      setRecharging(true);
      setError('');
      
      const response = await api.post(`/credits/creators/${userId}/recharge`, {
        credits: credits
      });
      
      if (response.data.success) {
        onRechargeSuccess(response.data);
        onClose();
      }
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to recharge credits');
    } finally {
      setRecharging(false);
    }
  };

  const getPackageColor = (packageType) => {
    switch (packageType) {
      case 'Basic': return 'border-gray-300 bg-white';
      case 'Popular': return 'border-blue-500 bg-blue-50 ring-2 ring-blue-500';
      case 'Value': return 'border-green-500 bg-green-50';
      case 'Premium': return 'border-purple-500 bg-purple-50';
      default: return 'border-gray-300 bg-white';
    }
  };

  const formatCurrency = (amount) => {
    return `₹${amount?.toLocaleString('en-IN')}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto modal-mobile">
        <div className="p-4 sm:p-6">
          <div className="flex justify-between items-center mb-4 sm:mb-6">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Recharge Credits</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-1 touch-target"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-blue-50 rounded-lg">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <p className="text-xs sm:text-sm text-gray-600">Current Credits</p>
                <p className="text-xl sm:text-2xl font-bold text-blue-600">{currentCredits}</p>
              </div>
              <div className="text-left sm:text-right">
                <p className="text-xs sm:text-sm text-gray-600">Credit Price</p>
                <p className="text-base sm:text-lg font-semibold">₹20 per credit</p>
              </div>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto loading-pulse"></div>
              <p className="mt-2 text-gray-600 text-sm">Loading packages...</p>
            </div>
          ) : (
            <>
              {/* Predefined Packages */}
              <div className="mb-6 sm:mb-8">
                <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Choose a Package</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                  {packages.map((pkg, index) => (
                    <div
                      key={index}
                      className={`p-3 sm:p-4 rounded-lg border-2 cursor-pointer transition-all hover:shadow-md mobile-card touch-target ${getPackageColor(pkg.packageType)} ${
                        selectedPackage === index ? 'ring-2 ring-blue-500' : ''
                      }`}
                      onClick={() => setSelectedPackage(index)}
                    >
                      <div className="text-center">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-xs sm:text-sm font-medium text-gray-600">{pkg.packageType}</span>
                          {pkg.discount > 0 && (
                            <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                              {pkg.discount}% OFF
                            </span>
                          )}
                        </div>
                        <div className="text-2xl font-bold text-gray-900 mb-1">
                          {pkg.credits} Credits
                        </div>
                        <div className="mb-2">
                          {pkg.savings > 0 && (
                            <div className="text-sm text-gray-500 line-through">
                              {formatCurrency(pkg.originalAmount)}
                            </div>
                          )}
                          <div className="text-lg font-bold text-green-600">
                            {formatCurrency(pkg.finalAmount)}
                          </div>
                        </div>
                        {pkg.savings > 0 && (
                          <div className="text-sm text-green-600 font-medium">
                            Save {formatCurrency(pkg.savings)}
                          </div>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRecharge(pkg.credits);
                          }}
                          disabled={recharging}
                          className="mt-3 w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {recharging ? 'Processing...' : 'Recharge'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Custom Amount */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4">Custom Amount</h3>
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Number of Credits (Minimum 30)
                    </label>
                    <input
                      type="number"
                      min="30"
                      value={customCredits}
                      onChange={(e) => setCustomCredits(e.target.value)}
                      placeholder="Enter credits amount"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  {calculation && (
                    <div className="flex-1">
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span>Credits:</span>
                            <span className="font-medium">{calculation.credits}</span>
                          </div>
                          {calculation.savings > 0 && (
                            <>
                              <div className="flex justify-between text-gray-500">
                                <span>Original Amount:</span>
                                <span className="line-through">{formatCurrency(calculation.originalAmount)}</span>
                              </div>
                              <div className="flex justify-between text-green-600">
                                <span>Discount ({calculation.discount}%):</span>
                                <span>-{formatCurrency(calculation.savings)}</span>
                              </div>
                            </>
                          )}
                          <div className="flex justify-between text-lg font-bold border-t pt-2">
                            <span>Total Amount:</span>
                            <span className="text-green-600">{formatCurrency(calculation.finalAmount)}</span>
                          </div>
                        </div>
                        <button
                          onClick={() => handleRecharge(calculation.credits)}
                          disabled={recharging || !calculation}
                          className="mt-3 w-full bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {recharging ? 'Processing...' : `Recharge ${calculation.credits} Credits`}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Discount Information */}
              <div className="mt-6 p-4 bg-yellow-50 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">💰 Discount Offers</h4>
                <div className="text-sm text-gray-600 space-y-1">
                  <p>• 100-299 credits: <span className="font-medium text-green-600">10% discount</span></p>
                  <p>• 300-499 credits: <span className="font-medium text-green-600">15% discount</span></p>
                  <p>• 500+ credits: <span className="font-medium text-green-600">25% discount</span></p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreditRechargeModal;

